import { CircleCheckBig } from 'lucide-react';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';
import { chartColours } from '../../constants';
import { ChartTooltip } from '../../home/org-dashboard/charts/ChartTooltip';

interface ClinicsBarChartProps {
  data: Array<{ label: string; value: number }>;
  referenceLines?: ClinicsBarChartReferenceLine[];
  labelFormatter?: (value: number) => string;
  tickFormatter?: (value: number) => string;
}

interface ClinicsBarChartReferenceLine {
  value: number;
  label: string;
  targetBarLabel?: string;
}

export function ClinicsBarChart({ data, referenceLines, labelFormatter, tickFormatter }: ClinicsBarChartProps) {
  // If a reference line is greater than the max data value, extend the axis width to show it
  const domain: AxisDomain | undefined = useMemo(() => {
    const dataMin = 0;

    let dataMax: string | number = 'dataMax';
    referenceLines?.forEach((referenceLine) => {
      const maxDataValue = Math.max(...data.map((d) => d.value));
      if (referenceLine.value >= maxDataValue) {
        dataMax = Math.ceil(referenceLine.value * 1.1);
      }
    });

    return [dataMin, dataMax];
  }, [data, referenceLines]);

  const height = calculateChartHeight(data);
  const barHeight = height / data.length;

  return (
    <div className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 48, right: 100 }} accessibilityLayer>
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            stroke="#fff"
            fontSize={14}
            tickFormatter={tickFormatter}
            domain={domain}
          />

          <YAxis dataKey="label" type="category" tickLine={false} fontSize={14} axisLine={false} stroke="#fff" />

          <CartesianGrid horizontal={false} strokeDasharray="3 3" strokeOpacity={0.2} stroke="#fff" />

          <Bar dataKey="value" radius={5} fill="#fff" isAnimationActive={false}>
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={chartColours[index % chartColours.length] ?? '#fff'} radius={0} />
            ))}

            <LabelList dataKey="value" position="right" fontSize={14} fill="#fff" formatter={labelFormatter} />
          </Bar>

          <Tooltip
            content={<ChartTooltip valueFormatter={(value) => labelFormatter?.(value) ?? value} />}
            cursor={{ fill: chartColours[0], fillOpacity: 0.1 }}
          />

          {referenceLines?.map((referenceLine) => {
            const value = data.find((d) => d.label === referenceLine.targetBarLabel)?.value;
            const color = getReferenceLineColor(referenceLine, value);
            return (
              <ReferenceLine
                key={`reference-line-${referenceLine.value}`}
                x={referenceLine.targetBarLabel ? undefined : referenceLine.value}
                segment={
                  referenceLine.targetBarLabel
                    ? [
                        { x: referenceLine.value, y: referenceLine.targetBarLabel },
                        { x: referenceLine.value, y: referenceLine.targetBarLabel }
                      ]
                    : undefined
                }
                stroke={color}
                strokeDasharray="4 4"
                strokeWidth={2}
                label={
                  referenceLine.targetBarLabel
                    ? (props: { viewBox: { x: number; y: number } }) => {
                        const xPos = props.viewBox.x;
                        const yPos = props.viewBox.y;
                        const targetMet = value !== undefined && value >= referenceLine.value;

                        return (
                          <>
                            <line
                              x1={xPos}
                              x2={xPos}
                              y1={yPos - barHeight / 3}
                              y2={yPos + barHeight / 3}
                              stroke={color}
                              strokeDasharray="4 4"
                              strokeWidth={2}
                            />
                            {targetMet && (
                              <CircleCheckBig color={color} x={xPos + 8} y={yPos - barHeight / 3} width={16} />
                            )}
                            <text
                              x={xPos + (targetMet ? 28 : 8)}
                              y={yPos - barHeight / 5}
                              fontWeight={targetMet ? 'bold' : 'initial'}
                              fill={color}
                              fontSize={14}
                              textAnchor="start"
                            >
                              {`${referenceLine.label} (${labelFormatter?.(referenceLine.value) ?? referenceLine.value})`}
                            </text>
                          </>
                        );
                      }
                    : {
                        value: `${referenceLine.label} (${labelFormatter?.(referenceLine.value) ?? referenceLine.value})`,
                        position: 'right',
                        fill: color,
                        fontSize: 14,
                        offset: 8
                      }
                }
              />
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function calculateChartHeight(data: Array<{ label: string; value: number }>) {
  let barHeightPx = 120;

  if (data.length > 25) {
    barHeightPx = 40;
  } else if (data.length > 10) {
    barHeightPx = 60;
  } else if (data.length > 5) {
    barHeightPx = 80;
  }

  return data.length * barHeightPx;
}

function getReferenceLineColor(referenceLine: ClinicsBarChartReferenceLine, value?: number): string {
  if (value !== undefined && value >= referenceLine.value) {
    return 'var(--color-success)';
  }

  return 'var(--color-accent)';
}
